import { IconButton } from "@/components-v2/Button";
import { Icon, IconProps } from "@/components-v2/Icon";
import { Spinner } from "@/components-v2/LoadingIndicator";
import { SelectItem, SelectItemProps } from "@/components-v2/Select";
import { isTMessage, isTMessageParam, TMessage } from "@/i18n/tt";
import { useTranslation } from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import { preventDefault } from "@/utils/events";
import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { isNil } from "lodash";
import {
  FocusEventHandler,
  InputHTMLAttributes,
  ReactElement,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import { defaultFilterFn, showsSelectedValue } from "./autocompleteHelpers";
import { useElementId } from "@/utils/reactHelpers";
import { renderIcon } from "../iconHelpers";

export type AutocompleteInputUIProps = {
  open: boolean;
  icon?: SelectItemProps["icon"];
  onClear?: () => void;
  error?: boolean;
  loading?: boolean;
  hideCaret?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  onInputBlur?: FocusEventHandler<HTMLInputElement>;
};

export type AutocompleteAction = {
  label: string;
  onSelect: () => void;
};

export type AutocompleteClearReason = "clear-press" | "input-clear";

const ACTION_ITEM = {};

function isActionItem(item: unknown): boolean {
  return item === ACTION_ITEM;
}

export type AutocompleteProps<T> = {
  id?: string;
  label?: string | TMessage;
  placeholder?: string;
  disabled?: boolean;
  hideCaret?: boolean;
  icon?: IconProps["name"];
  name?: string;
  value?: T | null;
  inputValue?: string;
  renderInputValue?: (item: T) => string;
  items: T[];
  action?: AutocompleteAction;
  onSelect: (item: T) => void;
  onClear?: (reason: AutocompleteClearReason) => void;
  onInputValueChange?: (value: string) => void;
  onInputBlur?: FocusEventHandler<HTMLInputElement>;
  idFn: (item: T) => string;
  labelFn: (item: T) => string;
  iconFn?: (item: T) => SelectItemProps["icon"];
  captionFn?: (item: T) => SelectItemProps["caption"];
  filterFn?: (
    { items, labelFn }: { items: T[]; labelFn: (item: T) => string },
    inputValue: string,
  ) => T[];
  fullWidth?: boolean;
  inline?: boolean;
  error?: boolean;
  message?: string;
  autofocus?: boolean;
  autocomplete?: string;
  /** Note: the rendered component MUST include an <AutocompleteInput/> element, not a regular <input/> element */
  renderInput?: (props: AutocompleteInputUIProps) => ReactElement;
  loading?: boolean;
  minQueryLength?: number;
};

function AutocompleteInputUI(props: AutocompleteInputUIProps) {
  const { t } = useTranslation();
  return (
    <div
      className={classNames(
        "relative",
        "flex",
        "items-center",
        "gap-2",
        "px-4",
        "leading-tight",
        "border",
        "rounded-sm",
        "focus-within:ring-1",
        "w-full",
        "cursor-pointer",
        props.error
          ? [
              "color-default",
              "bg-default",
              "border-error",
              "focus-within:border-error",
              "focus-within:ring-error",
              "hover:border-error",
              "hover:focus-within:border-error",
            ]
          : props.inputProps?.disabled
            ? ["subtle", "bg-gray-x-soft", "border-gray-soft", "hover:border-gray-soft"]
            : [
                "color-default",
                "bg-default",
                "border-gray",
                "focus-within:border-primary",
                "focus-within:ring-primary",
                "hover:border-gray-hard",
                "hover:focus-within:border-primary",
              ],
      )}
    >
      {props.icon && (
        <div className={classNames("text-xl leading-5 h-5 subtle")}>{renderIcon(props.icon)}</div>
      )}
      <BaseCombobox.Input
        {...props.inputProps}
        data-testid={"selectInput"}
        className={
          props.inputProps?.className ??
          "flex-1 focus-visible:outline-hidden py-3 w-full bg-transparent disabled:bg-transparent text-left whitespace-nowrap"
        }
      />
      {!props.inputProps?.disabled && !props.open && props.onClear && props.inputProps?.value && (
        <div>
          <IconButton
            color={"inherit"}
            icon={"x"}
            tooltip={t("clear")}
            onClick={preventDefault(props.onClear)}
          />
        </div>
      )}
      {props.loading && <Spinner />}
      {!props.inputProps?.disabled && !props.hideCaret && (
        <div
          data-testid={"selectCaret"}
          className={classNames("text-xl leading-5 h-5 pointer-events-none")}
        >
          <Icon name={props.open ? "caret-up" : "caret-down"} />
        </div>
      )}
    </div>
  );
}

export function Autocomplete<T>({ renderInputValue, ...props }: AutocompleteProps<T>) {
  const { t, tf } = useTranslation();
  const { labelFn, value, hideCaret } = props;
  const id = useElementId(props.id);
  const [inputValue, setInputValue] = useState(
    props.inputValue !== undefined
      ? props.inputValue
      : !isNil(value)
        ? renderInputValue
          ? renderInputValue(value)
          : labelFn(value)
        : "",
  );

  const isControlled = props.inputValue !== undefined;
  const filteredItems = isControlled
    ? props.items
    : (props.filterFn?.(props, inputValue) ?? defaultFilterFn(props, inputValue));

  const minQueryLength = props.minQueryLength ?? 0;
  const belowMinQueryLength = inputValue.trim().length < minQueryLength;

  const inputShowsSelectedValue = showsSelectedValue(
    { value, renderInputValue, labelFn },
    inputValue,
  );

  const handleOpenChange = (open: boolean, eventDetails: BaseCombobox.Root.ChangeEventDetails) => {
    if (open && inputShowsSelectedValue && eventDetails.reason !== "input-change") {
      eventDetails.cancel();
    }
  };

  const selectAllInputText = (event: SyntheticEvent<HTMLInputElement>) => {
    if (inputShowsSelectedValue) {
      event.currentTarget.select();
    }
  };

  const handleInputValueChange = (
    inputValue: string,
    event: BaseCombobox.Root.ChangeEventDetails,
  ) => {
    switch (event.reason) {
      case "input-change":
      case "input-clear":
        if (isControlled) {
          props.onInputValueChange?.(inputValue);
        } else {
          setInputValue(inputValue);
        }
        break;
    }
  };

  const handleChange = (item: T | null, eventDetails: BaseCombobox.Root.ChangeEventDetails) => {
    if (isActionItem(item)) {
      props.action?.onSelect();
      return;
    }
    if (item === null) {
      props.onClear?.(eventDetails.reason === "input-clear" ? "input-clear" : "clear-press");
      if (!isControlled) {
        setInputValue("");
      }
    } else {
      props.onSelect(item);
      if (!isControlled) {
        setInputValue(renderInputValue ? renderInputValue(item) : labelFn(item));
      }
    }
  };

  const handleClear =
    props.onClear &&
    (() => {
      props.onClear?.("clear-press");
      if (!isControlled) {
        setInputValue("");
      }
    });

  useEffect(() => {
    if (props.inputValue !== undefined) {
      setInputValue(props.inputValue);
    } else {
      setInputValue(value ? (renderInputValue ? renderInputValue(value) : labelFn(value)) : "");
    }
    // Note: we only want to react to changes of `value`, and `labelFn` might always change, so don't include it in the deps list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, props.inputValue]);

  return (
    <BaseCombobox.Root
      items={filteredItems}
      value={value ?? null}
      filteredItems={filteredItems}
      onValueChange={handleChange}
      inputValue={inputValue}
      onInputValueChange={handleInputValueChange}
      onOpenChange={handleOpenChange}
      disabled={props.disabled}
      defaultOpen={props.autofocus && !inputShowsSelectedValue}
      autoHighlight
    >
      <div className={classNames(props.fullWidth && "w-full", props.inline && "w-60")}>
        {props.label && (
          <label htmlFor={id} className={"block mb-1"}>
            {isTMessage(props.label) || isTMessageParam(props.label)
              ? tf(props.label)
              : props.label}
          </label>
        )}
        <BaseCombobox.InputGroup
          render={(inputGroupProps, state) => (
            <div {...inputGroupProps}>
              <AutocompleteInputUI
                open={state.open}
                icon={props.icon ?? (value ? props.iconFn?.(value) : undefined)}
                error={props.error}
                onClear={handleClear}
                hideCaret={inputShowsSelectedValue || hideCaret}
                inputProps={{
                  id: id,
                  value: inputValue,
                  name: props.name,
                  placeholder: props.placeholder,
                  disabled: props.disabled,
                  autoFocus: props.autofocus,
                  autoComplete: props.autocomplete ?? "off",
                  onBlur: props.onInputBlur,
                  onFocus: selectAllInputText,
                  onClick: selectAllInputText,
                }}
                loading={props.loading}
              />
            </div>
          )}
        />
        {props.message && (
          <div
            className={classNames(
              "pt-1",
              "pl-4",
              "text-sm",
              props.error ? "color-error" : "subtle",
            )}
          >
            {props.message}
          </div>
        )}
      </div>
      <BaseCombobox.Portal>
        <BaseCombobox.Positioner align="start">
          <BaseCombobox.Popup
            style={{
              transformOrigin: "var(--transform-origin)",
              maxHeight: "var(--available-height)",
              minWidth: "var(--anchor-width)",
            }}
            className={classNames(
              "rounded-sm",
              "border",
              "border-gray",
              "shadow-lg",
              "bg-default",
              "py-1",
              "overflow-auto",
            )}
          >
            {!props.loading && (
              <BaseCombobox.Empty>
                <SelectItem
                  label={belowMinQueryLength ? t("autocomplete.min-query-length") : t("noOptions")}
                />
              </BaseCombobox.Empty>
            )}
            <BaseCombobox.List>
              {(value) => (
                <BaseCombobox.Item
                  key={props.idFn(value)}
                  value={value}
                  render={(itemProps, state) => (
                    <div {...itemProps}>
                      <SelectItem
                        label={labelFn(value)}
                        icon={props.iconFn?.(value)}
                        caption={props.captionFn?.(value)}
                        active={state.highlighted}
                      />
                    </div>
                  )}
                />
              )}
            </BaseCombobox.List>
            {!props.loading && props.action && (
              <BaseCombobox.Item
                value={ACTION_ITEM}
                render={(itemProps, state) => (
                  <div {...itemProps}>
                    <SelectItem label={props.action?.label} active={state.highlighted} />
                  </div>
                )}
              />
            )}
          </BaseCombobox.Popup>
        </BaseCombobox.Positioner>
      </BaseCombobox.Portal>
    </BaseCombobox.Root>
  );
}
